/**
 * Returns whether the `value` is defined. (not null nor undefined)
 */
export const isDefined = <T>(value: T | null | undefined): value is T => value !== null && value !== undefined;

/**
 * Asserts that `value` is defined, throws error if not.
 */

export const assertIsDefined: <T>(value: T | null | undefined, name?: string) => asserts value is NonNullable<T> = <
  T extends NonNullable<unknown>,
>(
  value: T | null | undefined,
  name: string = typeof value,
): asserts value is NonNullable<T> => {
  if (!isDefined(value)) throw new Error(`${name} not defined`);
};

/**
 * Type guard to determine if a string is a valid value for an enum.
 *
 * Used as a higher order function, e.g. `isSomeEnum(EnumType)(inputString)`.
 */
export const isSomeEnum =
  <T extends Record<string, unknown>>(enumParam: T) =>
  (token: unknown): token is T[keyof T] =>
    Object.values(enumParam).includes(token as T[keyof T]);

export const assertIsDefinedWithCallback: <T>(value: T | null | undefined, callback: () => void) => asserts value is NonNullable<T> = <
  T extends NonNullable<unknown>,
>(
  value: T | null | undefined,
  callback: () => void,
): asserts value is NonNullable<T> => {
  if (!isDefined(value)) callback();
};

export type NoUndefined<T> = T extends undefined ? never : T;
